/**
 * Analytics Wrapper
 * Story 355: Web analytics SDK integration
 *
 * Provides typed event tracking with automatic source and environment injection.
 * Events are sent to PostHog when configured; gracefully degrades to no-op.
 */
import { trackEvent } from './posthog';

/**
 * Environment detection based on hostname
 */
type Environment = 'local' | 'staging' | 'production';

function getEnvironment(): Environment {
  // FALLBACK: SSR/test contexts return 'production' but this is safe because
  // trackEvent() guards with posthogConfig.apiKey check - no events are sent without key
  if (typeof window === 'undefined') return 'production';

  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  if (hostname.includes('staging')) {
    return 'staging';
  }
  return 'production';
}

/**
 * Base properties added to all events automatically
 */
interface BaseEventProperties {
  source: 'web';
  environment: Environment;
}

/**
 * Analytics event definition
 *
 * Event types will be extended by Story 356 (Web event instrumentation)
 * to include specific events for stories, tasks, sections, etc.
 */
export interface AnalyticsEvent<T extends string = string, P = Record<string, unknown>> {
  name: T;
  properties?: P;
}

// ============================================
// Story Events (Story 356 will instrument these)
// ============================================

export interface StoryCreatedEvent extends AnalyticsEvent<'story_created'> {
  properties: { story_id: string; title: string };
}

export interface StoryUpdatedEvent extends AnalyticsEvent<'story_updated'> {
  properties: { story_id: string; fields_changed: string[] };
}

export interface StoryDeletedEvent extends AnalyticsEvent<'story_deleted'> {
  properties: { story_id: string };
}

export interface StoryReorderedEvent extends AnalyticsEvent<'story_reordered'> {
  properties: { story_id: string; target_epic?: string; priority: number };
}

export interface StoryStatusChangedEvent extends AnalyticsEvent<'story_status_changed'> {
  properties: { story_id: string; from_status: string; to_status: string };
}

// ============================================
// Task Events
// ============================================

export interface TaskCreatedEvent extends AnalyticsEvent<'task_created'> {
  properties: { story_id: string; seq: number; task_type?: string };
}

export interface TaskUpdatedEvent extends AnalyticsEvent<'task_updated'> {
  properties: { story_id: string; seq: number; fields_changed: string[] };
}

export interface TaskReorderedEvent extends AnalyticsEvent<'task_reordered'> {
  properties: { story_id: string; new_order: number[] };
}

export interface TaskDeletedEvent extends AnalyticsEvent<'task_deleted'> {
  properties: { story_id: string; seq: number };
}

export interface TaskRestoredEvent extends AnalyticsEvent<'task_restored'> {
  properties: { story_id: string; seq: number };
}

// ============================================
// Section Events
// ============================================

export interface SectionCreatedEvent extends AnalyticsEvent<'section_created'> {
  properties: { story_id: string; doc_type: string; section_key: string };
}

export interface SectionUpdatedEvent extends AnalyticsEvent<'section_updated'> {
  properties: { story_id: string; doc_type: string; section_key: string; fields: string[] };
}

export interface SectionDeletedEvent extends AnalyticsEvent<'section_deleted'> {
  properties: { story_id: string; doc_type: string; section_key: string };
}

export interface SectionReorderedEvent extends AnalyticsEvent<'section_reordered'> {
  properties: { story_id: string; doc_type: string; new_order: string[] };
}

// ============================================
// Epic Events
// ============================================

export interface EpicCreatedEvent extends AnalyticsEvent<'epic_created'> {
  properties: { slug: string; title: string };
}

export interface EpicUpdatedEvent extends AnalyticsEvent<'epic_updated'> {
  properties: { slug: string; fields_changed: string[] };
}

export interface EpicDeletedEvent extends AnalyticsEvent<'epic_deleted'> {
  properties: { slug: string };
}

export interface EpicReorderedEvent extends AnalyticsEvent<'epic_reordered'> {
  properties: { slug: string; new_position: number };
}

// ============================================
// Navigation Events
// ============================================

export interface TabSwitchedEvent extends AnalyticsEvent<'tab_switched'> {
  properties: { story_id?: string; tab: string };
}

export interface SearchExecutedEvent extends AnalyticsEvent<'search_executed'> {
  properties: { query: string; result_count: number };
}

export interface ProjectSwitchedEvent extends AnalyticsEvent<'project_switched'> {
  properties: { from_project?: string; to_project: string };
}

/**
 * Union of all typed analytics events
 */
export type TypedAnalyticsEvent =
  | StoryCreatedEvent
  | StoryUpdatedEvent
  | StoryDeletedEvent
  | StoryReorderedEvent
  | StoryStatusChangedEvent
  | TaskCreatedEvent
  | TaskUpdatedEvent
  | TaskReorderedEvent
  | TaskDeletedEvent
  | TaskRestoredEvent
  | SectionCreatedEvent
  | SectionUpdatedEvent
  | SectionDeletedEvent
  | SectionReorderedEvent
  | EpicCreatedEvent
  | EpicUpdatedEvent
  | EpicDeletedEvent
  | EpicReorderedEvent
  | TabSwitchedEvent
  | SearchExecutedEvent
  | ProjectSwitchedEvent;

/**
 * Track a typed analytics event
 *
 * Automatically adds:
 * - source: 'web' (distinguishes from MCP events)
 * - environment: 'local' | 'staging' | 'production'
 *
 * @param event - Typed analytics event
 *
 * @example
 * track({ name: 'story_created', properties: { story_id: '123', title: 'New Story' } });
 */
export function track<T extends TypedAnalyticsEvent>(event: T): void {
  const baseProperties: BaseEventProperties = {
    source: 'web',
    environment: getEnvironment(),
  };

  const enrichedProperties = {
    ...baseProperties,
    ...event.properties,
  };

  trackEvent(event.name, enrichedProperties);
}

/**
 * Track an untyped event (escape hatch for one-off events)
 *
 * Prefer using the typed `track()` function when possible.
 */
export function trackUntyped(eventName: string, properties?: Record<string, unknown>): void {
  const baseProperties: BaseEventProperties = {
    source: 'web',
    environment: getEnvironment(),
  };

  trackEvent(eventName, { ...baseProperties, ...properties });
}
